import React from 'react';
import { Card, CardContent, Typography, Grid, Box } from '@mui/material';

interface RepricerMetricsProps {
  data: any;
  lastUpdated?: string;
}

export default function RepricerMetrics({ data, lastUpdated }: RepricerMetricsProps) {
  if (!data || !data.success) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Repricer Dashboard</Typography>
        <Typography color="error">
          {data?.error || 'No metrics data available'}
        </Typography>
      </Box>
    );
  }

  const metrics = data.data;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Repricer Dashboard</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {lastUpdated ? `Last updated: ${new Date(lastUpdated).toLocaleString()}` : 'Loading...'}
      </Typography>
      
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>🔔 Price Notifications (Last {metrics.hours_analyzed}h)</Typography>
              <Typography variant="h3" color="primary">
                {metrics.notifications_received?.toLocaleString() ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                SP-API offer-change notifications received
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>📊 Offer Changes (Last {metrics.hours_analyzed}h)</Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1">🔄 Internal: <strong>{metrics.internal_changes}</strong></Typography>
                <Typography variant="body1">🌐 External: <strong>{metrics.external_changes}</strong></Typography>
                <Typography variant="body1">⭐ Featured: <strong>{metrics.featured_changes}</strong></Typography>
                <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold' }}>
                  📈 Total: <strong>{(metrics.internal_changes + metrics.external_changes + metrics.featured_changes).toLocaleString()}</strong>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>📤 SP-API Updates (Last {metrics.hours_analyzed}h)</Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1">✅ Successful: <strong>{metrics.spapi_updates?.toLocaleString()}</strong></Typography>
                <Typography variant="body1">🎯 Unique ASINs: <strong>{metrics.unique_asins?.toLocaleString()}</strong></Typography>
                <Typography variant="body1">🚫 Throttled: <strong>{metrics.throttled_requests}</strong></Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  📊 Rate: <strong>{metrics.update_rate?.toFixed(1)} updates/hour</strong>
                </Typography>
                <Typography variant="body1">
                  ✅ Success: <strong>{metrics.success_rate?.toFixed(1)}%</strong>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>📥 Queue Status</Typography>
              <Typography variant="h3" color="primary">
                {metrics.queue_messages?.toLocaleString() || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Messages in queue
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
